// ---------------------------------------------------------------------------
// Dock conversation persistence for the Ask-AI agent console.
//
// The agent hooks (useClaudeAgent / useCodexAgent) keep the conversation in
// React state only, so a browser refresh used to wipe the console. This module
// persists the transcript (messages + todos) and the backend session id to
// localStorage so the dock can restore the conversation on reload and — when a
// run is still active server-side — reattach to the SAME agent session. The
// backend keeps detached runs alive and buffers their events, so reattaching
// with the stored session id resumes live progress exactly where it left off.
//
// Keyed by engine + user + project so switching users on the same browser
// never leaks another user's conversation into the dock.
// ---------------------------------------------------------------------------

export interface PersistedAgentMessage {
  role: string
  content: string
  /** ISO timestamp (Date is serialized on save, revived on load). */
  timestamp: string
  tool?: string
  file?: string
  permissionDecision?: 'allow' | 'deny'
  ask?: { question: string; options: string[] }
  askAnswered?: boolean
  askAnsweredWith?: string
  /** role === 'activity': granular progress line level + accounting. */
  activityLevel?: string
  meta?: { elapsedS?: number; tokens?: number; costUsd?: number }
}

export interface PersistedAgentChat {
  version: 1
  sessionId: string | null
  messages: PersistedAgentMessage[]
  todos: { content: string; status: string; activeForm?: string }[]
  savedAt: number
}

/** Keep at most this many messages when persisting (oldest dropped first). */
const MAX_PERSISTED_MESSAGES = 300
/** Hard cap on the serialized payload (localStorage budget is ~5MB total). */
const MAX_PERSISTED_BYTES = 1_500_000

function storageKey(engine: string, userId: string, project: string): string {
  return `twasta_agent_chat:${engine}:${userId}:${project}`
}

export function loadAgentChat(
  engine: string,
  userId: string,
  project: string,
): PersistedAgentChat | null {
  try {
    const raw = localStorage.getItem(storageKey(engine, userId, project))
    if (!raw) return null
    const parsed = JSON.parse(raw) as PersistedAgentChat
    if (!parsed || parsed.version !== 1 || !Array.isArray(parsed.messages)) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export function saveAgentChat(
  engine: string,
  userId: string,
  project: string,
  data: Omit<PersistedAgentChat, 'version' | 'savedAt'>,
): void {
  try {
    let messages = data.messages
    if (messages.length > MAX_PERSISTED_MESSAGES) {
      messages = messages.slice(messages.length - MAX_PERSISTED_MESSAGES)
    }
    const payload: PersistedAgentChat = {
      version: 1,
      sessionId: data.sessionId,
      messages,
      todos: data.todos,
      savedAt: Date.now(),
    }
    let serialized = JSON.stringify(payload)
    // Trim oldest messages until the payload fits the budget.
    while (serialized.length > MAX_PERSISTED_BYTES && payload.messages.length > 1) {
      payload.messages = payload.messages.slice(
        Math.max(1, Math.floor(payload.messages.length / 4)),
      )
      serialized = JSON.stringify(payload)
    }
    localStorage.setItem(storageKey(engine, userId, project), serialized)
  } catch {
    // Quota exceeded or storage unavailable — persistence is best-effort;
    // the live conversation in React state is unaffected.
  }
}

export function clearAgentChat(engine: string, userId: string, project: string): void {
  try {
    localStorage.removeItem(storageKey(engine, userId, project))
  } catch {
    // ignore
  }
}

/**
 * Remove every persisted agent conversation for a project, across ALL engines
 * and users cached in this browser. Called when the project is deleted — a
 * NEW project created under the same name must start with a clean console,
 * not inherit the deleted app's transcript.
 */
export function clearAgentChatsForProject(project: string): void {
  try {
    const suffix = `:${project}`
    const doomed: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith('twasta_agent_chat:') && key.endsWith(suffix)) {
        doomed.push(key)
      }
    }
    doomed.forEach((key) => localStorage.removeItem(key))
  } catch {
    // ignore — persistence is best-effort
  }
}
